import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowLeft, MapPin, Check, X, Clock, Info, ShieldCheck, Loader2, Images, CalendarDays, Tag } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import Lightbox from "@/components/Lightbox";
import { publicServicesApi, enquiryApi } from "@/lib/api";
import { priceLabel, serviceCover, embedUrl, formatINR, BOOKING_TYPES, EVENT_TYPES } from "@/lib/serviceConstants";

const inputCls = "w-full rounded-xl border border-input bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-gold focus:ring-2 focus:ring-gold/20";

export default function ServiceDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [svc, setSvc] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [lb, setLb] = useState({ items: null, index: null });
  const [enqOpen, setEnqOpen] = useState(false);

  const load = useCallback(() => {
    publicServicesApi.get(slug).then(setSvc).catch(() => setNotFound(true));
  }, [slug]);
  useEffect(() => { load(); }, [load]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-background"><Navbar />
        <div className="mx-auto max-w-lg px-6 py-28 text-center">
          <h1 className="font-serif text-3xl font-semibold">Service not available</h1>
          <p className="mt-2 text-sm text-muted-foreground">This service may have been unpublished.</p>
          <Link to="/services" className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground"><ArrowLeft className="h-4 w-4" /> Back to services</Link>
        </div>
      </div>
    );
  }
  if (!svc) return <div className="min-h-screen bg-background"><Navbar /><div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div></div>;

  const cover = serviceCover(svc);
  const embed = embedUrl(svc.video_url);
  const bt = BOOKING_TYPES.find((b) => b.value === svc.booking_type) || BOOKING_TYPES[0];
  const pb = svc.price_breakdown || {};
  const gallery = svc.gallery || [];

  const ListBlock = ({ title, items, icon: Icon, positive }) => items?.length ? (
    <div>
      <p className="eyebrow mb-3 text-gold">{title}</p>
      <ul className="space-y-2">
        {items.map((it, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-foreground/80">
            <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${positive ? "text-emerald-600" : "text-rose-500"}`} /> {it}
          </li>
        ))}
      </ul>
    </div>
  ) : null;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="relative h-[42vh] min-h-[280px] w-full overflow-hidden bg-primary">
        {cover && <img src={cover} alt={svc.name} className="h-full w-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/30 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 mx-auto max-w-6xl px-5 pb-8 sm:px-8">
          <Link to="/services" className="mb-3 inline-flex items-center gap-1.5 text-sm text-white/80 hover:text-white"><ArrowLeft className="h-4 w-4" /> All services</Link>
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-gold px-3 py-1 text-xs font-semibold text-primary">{bt.cta}</span>
            <span className="rounded-full bg-white/15 px-3 py-1 text-xs font-medium text-white backdrop-blur">{svc.service_code}</span>
          </div>
          <h1 className="mt-2 font-serif text-4xl font-semibold text-white sm:text-5xl">{svc.name}</h1>
          {svc.short_description && <p className="mt-2 max-w-2xl text-white/85">{svc.short_description}</p>}
        </div>
      </div>

      <div className="mx-auto grid max-w-6xl gap-10 px-5 py-12 sm:px-8 lg:grid-cols-[1fr_340px]">
        <div className="space-y-10">
          {svc.full_description && (
            <div>
              <p className="eyebrow mb-2 text-gold">About this service</p>
              <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground/80">{svc.full_description}</p>
            </div>
          )}

          {gallery.length > 0 && (
            <div>
              <p className="eyebrow mb-3 text-gold">Gallery</p>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {gallery.map((m, i) => (
                  <button key={m.id || i} onClick={() => setLb({ items: gallery, index: i })} data-testid={`service-photo-${i}`} className="group relative aspect-square overflow-hidden rounded-xl bg-secondary">
                    {m.type === "video" ? <video src={m.url} className="h-full w-full object-cover" muted /> : <img src={m.url} alt={m.alt || svc.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />}
                  </button>
                ))}
              </div>
            </div>
          )}

          {embed && (
            <div>
              <p className="eyebrow mb-3 text-gold">Video</p>
              <div className="aspect-video overflow-hidden rounded-2xl border border-border"><iframe src={embed} title="Service video" className="h-full w-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen /></div>
            </div>
          )}

          <div className="grid gap-8 sm:grid-cols-2">
            <ListBlock title="What's included" items={svc.inclusions} icon={Check} positive />
            <ListBlock title="Not included" items={svc.exclusions} icon={X} />
          </div>

          {svc.features?.length > 0 && (
            <div>
              <p className="eyebrow mb-3 text-gold">Highlights</p>
              <div className="flex flex-wrap gap-2">{svc.features.map((f, i) => <span key={i} className="rounded-full border border-border bg-card px-3 py-1.5 text-sm">{f}</span>)}</div>
            </div>
          )}

          {(svc.customer_instructions || svc.cancellation_policy || svc.booking_lead_time) && (
            <div className="space-y-4 rounded-2xl bg-secondary/50 p-6">
              {svc.booking_lead_time && <p className="flex items-start gap-2 text-sm"><Clock className="mt-0.5 h-4 w-4 text-gold" /><span><b>Booking lead time:</b> {svc.booking_lead_time}</span></p>}
              {svc.customer_instructions && <p className="flex items-start gap-2 text-sm"><Info className="mt-0.5 h-4 w-4 text-gold" /><span><b>Good to know:</b> {svc.customer_instructions}</span></p>}
              {svc.cancellation_policy && <p className="flex items-start gap-2 text-sm"><ShieldCheck className="mt-0.5 h-4 w-4 text-gold" /><span><b>Cancellation:</b> {svc.cancellation_policy}</span></p>}
            </div>
          )}

          {svc.locations?.length > 0 && (
            <p className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground"><MapPin className="h-4 w-4 text-gold" /> Available in: {svc.locations.join(", ")}</p>
          )}
          {svc.tags?.length > 0 && (
            <p className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground"><Tag className="h-4 w-4 text-gold" /> {svc.tags.join(", ")}</p>
          )}
        </div>

        {/* Pricing / CTA */}
        <aside className="lg:sticky lg:top-24 lg:self-start">
          <div className="rounded-3xl border border-border bg-card p-6 shadow-sm">
            <p className="text-sm text-muted-foreground">Pricing</p>
            <p className="mt-1 font-serif text-3xl font-semibold text-foreground">{priceLabel(svc)}</p>
            {svc.gst_applicable && svc.price ? (
              <div className="mt-3 space-y-1 border-t border-border pt-3 text-sm text-muted-foreground">
                <div className="flex justify-between"><span>Base</span><span>{formatINR(pb.taxable)}</span></div>
                <div className="flex justify-between"><span>GST ({pb.gst_rate}%)</span><span>{formatINR(pb.tax)}</span></div>
                <div className="flex justify-between font-semibold text-foreground"><span>Total</span><span>{formatINR(pb.total)}</span></div>
              </div>
            ) : null}
            <button onClick={() => setEnqOpen(true)} data-testid="service-cta-btn" className="mt-5 w-full rounded-full bg-primary py-3.5 text-sm font-semibold text-primary-foreground transition-all hover:shadow-lg">{bt.cta}</button>
            <button onClick={() => setEnqOpen(true)} data-testid="service-contact-btn" className="mt-2 w-full rounded-full border border-border py-3 text-sm font-semibold transition-colors hover:bg-secondary">Contact Godinga</button>
            <p className="mt-3 text-center text-xs text-muted-foreground">Advance payment & instant online booking coming soon. Submit an enquiry and our team will confirm availability.</p>
          </div>
        </aside>
      </div>

      <Lightbox items={lb.items} index={lb.index} onClose={() => setLb({ items: null, index: null })} onIndex={(i) => setLb((s) => ({ ...s, index: i }))} />
      {enqOpen && <EnquiryModal svc={svc} onClose={() => setEnqOpen(false)} onDone={() => { setEnqOpen(false); navigate("/services"); }} />}
    </div>
  );
}

function EnquiryModal({ svc, onClose, onDone }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", event_type: svc.event_types?.[0] || "Wedding", date: "", location: "", guests: "", budget: "", details: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    if (!f.name.trim() || !f.email.trim() || !f.phone.trim()) { toast.error("Please add your name, email and phone"); return; }
    setBusy(true);
    try {
      await enquiryApi.create({
        ...f,
        guests: f.guests ? parseInt(f.guests, 10) : 0,
        details: `Service enquiry: ${svc.name} (${svc.service_code}).\n${f.details}`.trim(),
      });
      toast.success("Enquiry sent! Our team will reach out shortly.");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send enquiry");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4 py-10" onClick={onClose} data-testid="service-enquiry-modal">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-lg rounded-3xl bg-card p-6 shadow-2xl sm:p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <p className="eyebrow text-gold">Enquiry</p>
            <h2 className="mt-1 font-serif text-2xl font-semibold">{svc.name}</h2>
          </div>
          <button onClick={onClose} className="rounded-full p-2 hover:bg-secondary"><X className="h-5 w-5" /></button>
        </div>
        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          <input data-testid="enq-name" className={inputCls} placeholder="Your name *" value={f.name} onChange={(e) => set("name", e.target.value)} />
          <input data-testid="enq-phone" className={inputCls} placeholder="Phone *" value={f.phone} onChange={(e) => set("phone", e.target.value)} />
          <input data-testid="enq-email" className={`${inputCls} sm:col-span-2`} placeholder="Email *" value={f.email} onChange={(e) => set("email", e.target.value)} />
          <select data-testid="enq-event-type" className={inputCls} value={f.event_type} onChange={(e) => set("event_type", e.target.value)}>{EVENT_TYPES.map((t) => <option key={t}>{t}</option>)}</select>
          <input data-testid="enq-date" type="date" className={inputCls} value={f.date} onChange={(e) => set("date", e.target.value)} />
          <input data-testid="enq-location" className={inputCls} placeholder="Event location / city" value={f.location} onChange={(e) => set("location", e.target.value)} />
          <input data-testid="enq-guests" type="number" className={inputCls} placeholder="Expected guests" value={f.guests} onChange={(e) => set("guests", e.target.value)} />
          <input data-testid="enq-budget" className={`${inputCls} sm:col-span-2`} placeholder="Budget range (optional)" value={f.budget} onChange={(e) => set("budget", e.target.value)} />
          <textarea data-testid="enq-details" rows={3} className={`${inputCls} sm:col-span-2`} placeholder="Requirements & special requests" value={f.details} onChange={(e) => set("details", e.target.value)} />
        </div>
        <button onClick={submit} disabled={busy} data-testid="enq-submit" className="mt-5 w-full rounded-full bg-primary py-3.5 text-sm font-semibold text-primary-foreground disabled:opacity-60">
          {busy ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : "Send enquiry"}
        </button>
      </motion.div>
    </div>
  );
}
